import React from "react";

import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";
import {TRANSFER_AUTH_TOKEN_MUTATION} from "@khan/auth-shared";
import ErrorLoading from "@khan/component-screens/error-loading";
import FullPageSpinner from "@khan/component-screens/full-page-spinner";
// eslint-disable-next-line no-restricted-imports -- TODO(FEI-7051): switch to useGql
import {useApolloGqlClient, type ApolloGqlClient} from "@khan/data-gql-apollo";
import {preserveMarketingParams, redirect} from "@khan/urls";

interface Props {
    continueURL: URL;
}

const addAuthTransferForSubdomain = async (
    gqlClient: ApolloGqlClient,
    continuePathname: string,
    continueSearch: string,
    hostname: string,
) => {
    // The user has landed from a different locale, so we create a transfer
    // auth token for them.
    const result = await gqlClient.mutate(TRANSFER_AUTH_TOKEN_MUTATION, {});

    const transferAuthToken = result.data?.createTransferAuthToken?.token;
    if (!transferAuthToken) {
        return;
    }

    return `https://${hostname}/transfer_auth?key=${transferAuthToken}&continue=${encodeURIComponent(
        continuePathname + continueSearch,
    )}`;
};

type State =
    | {
          status: "loading";
      }
    | {
          status: "error";
      }
    | {
          status: "success";
          transferAuthURL: string;
      };

/**
 * MaybeAutomaticallyLoginToSubdomain attempts to automatically log a user
 * into a subdomain using transfer auth.
 *
 * For a description of how the complete flow works please see
 * services/fastly-khanacademy-compute/handlers/webpage.go and read the
 * comment for `handleAutomaticallyLoginToSubdomain`.
 */
const MaybeAutomaticallyLoginToSubdomain = (props: Props) => {
    const [state, setState] = React.useState<State>({status: "loading"});
    const {gqlClient} = useApolloGqlClient();
    const {continueURL} = props;

    useOnMountEffect(() => {
        const action = async () => {
            try {
                const transferAuthURL = await addAuthTransferForSubdomain(
                    gqlClient,
                    continueURL.pathname,
                    continueURL.search,
                    continueURL.hostname,
                );
                if (transferAuthURL) {
                    setState({
                        status: "success",
                        transferAuthURL: transferAuthURL,
                    });
                } else {
                    setState({
                        status: "error",
                    });
                }
            } catch (e) {
                setState({
                    status: "error",
                });
            }
        };

        action();

        return () => {
            // TODO: check if we're still mounted
        };
    });

    switch (state.status) {
        case "loading": {
            return <FullPageSpinner />;
        }
        case "error": {
            return <ErrorLoading />;
        }
        case "success": {
            redirect(
                preserveMarketingParams(
                    state.transferAuthURL,
                    new URLSearchParams(location.search),
                ),
            );
            return <FullPageSpinner />;
        }
    }
};

export default MaybeAutomaticallyLoginToSubdomain;
